import { useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * Returns the date two Saturdays *strictly before* ``isoDate`` as
 * ``YYYY-MM-DD``. Used to default the music-submission deadline:
 * organisers want music in by the second-prior Saturday so they can
 * load it before the comp weekend.
 *
 *   * Sat 7 Nov  → 1st prior Sat = 31 Oct → returns 24 Oct
 *   * Sun 8 Nov  → 1st prior Sat =  7 Nov → returns 31 Oct
 *   * Mon 9 Nov  → 1st prior Sat =  7 Nov → returns 31 Oct
 */
const twoSaturdaysBefore = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  // JS getDay(): Sun=0, Mon=1, ..., Sat=6
  const dow = d.getDay();
  // Days back to the most recent Saturday *strictly* before d.
  // If d itself is a Saturday we still go back a full week.
  const daysBackFirst = dow === 6 ? 7 : (dow + 1);
  d.setDate(d.getDate() - daysBackFirst - 7);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function RegistrationInfoSection({ formData, setFormData, readOnly }) {
  // Default the music deadline to two Saturdays before the comp start.
  //
  // The trick is *when* to recompute:
  //   * On first load with no value yet → fill from start_date.
  //   * When the user changes start_date later → recompute, UNLESS
  //     they've manually edited the deadline field themselves.
  //
  // We track the previously-seen start_date in a ref so we know
  // whether this useEffect run is the "initial fill" or a "user
  // changed start_date" run. ``userEditedDeadlineRef`` flips true the
  // moment the deadline input fires its onChange; from that point we
  // never auto-overwrite, even if start_date changes again.
  const prevStartDateRef = useRef(formData.start_date || '');
  const userEditedDeadlineRef = useRef(false);

  useEffect(() => {
    const newStart = formData.start_date;
    const prevStart = prevStartDateRef.current;
    if (!newStart) {
      prevStartDateRef.current = '';
      return;
    }
    const expected = twoSaturdaysBefore(newStart);
    const currentDeadline = formData.music_submission_deadline || '';

    let shouldFill = false;
    if (!currentDeadline) {
      // No deadline yet — always auto-fill.
      shouldFill = true;
    } else if (prevStart && prevStart !== newStart && !userEditedDeadlineRef.current) {
      // Start-date moved AND the user hasn't manually edited the
      // deadline — refresh the auto value so it doesn't go stale.
      shouldFill = true;
    }

    if (shouldFill && currentDeadline !== expected) {
      setFormData((prev) => ({ ...prev, music_submission_deadline: expected }));
    }
    prevStartDateRef.current = newStart;
  }, [formData.start_date, formData.music_submission_deadline, setFormData]);

  return (
    <div className="p-4 border rounded-lg bg-green-50">
      <h4 className="font-medium mb-4 text-green-800">Registration Information</h4>
      <div className="space-y-4">
        {/* Row 1: Email */}
        <div className="flex items-center justify-between">
          <Label className="flex-shrink-0">Email address for music and content <span className="text-red-500">*</span></Label>
          <input
            type="email"
            className="w-96 border rounded-lg px-3 py-2"
            value={formData.music_email}
            onChange={(e) => setFormData({ ...formData, music_email: e.target.value })}
            disabled={readOnly}
            required
            data-testid="competition-music-email"
          />
        </div>
        {/* Row 1b: Music / Content sheets deadline */}
        <div className="flex items-center justify-between">
          <Label className="flex-shrink-0">Music/Content Sheets in by</Label>
          <input
            type="date"
            className="w-96 border rounded-lg px-3 py-2"
            value={formData.music_submission_deadline || ''}
            onChange={(e) => {
              userEditedDeadlineRef.current = true;
              setFormData({ ...formData, music_submission_deadline: e.target.value });
            }}
            disabled={readOnly}
            data-testid="competition-music-deadline"
          />
        </div>
        {/* Row 2: Bank account */}
        <div className="flex items-center justify-between">
          <Label className="flex-shrink-0">Bank account for payment <span className="text-red-500">*</span></Label>
          <input
            type="text"
            className="w-96 border rounded-lg px-3 py-2"
            value={formData.bank_account}
            onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
            disabled={readOnly}
            required
            data-testid="competition-bank-account"
          />
        </div>
        {/* Row 3: Account Holder Name */}
        <div className="flex items-center justify-between">
          <Label className="flex-shrink-0">Bank account - Account Holder Name <span className="text-red-500">*</span></Label>
          <input
            type="text"
            className="w-96 border rounded-lg px-3 py-2"
            value={formData.bank_account_name}
            onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
            disabled={readOnly}
            required
            data-testid="competition-bank-name"
          />
        </div>
        {/* Row 4: Confirmation checkbox */}
        <div className="flex items-center justify-between">
          <Label className="flex-shrink-0">Confirm Bank account for payment</Label>
          <div className="w-96 flex items-center gap-2">
            <Checkbox
              checked={formData.bank_account_confirmed}
              onCheckedChange={(val) => setFormData({ ...formData, bank_account_confirmed: val })}
              disabled={readOnly}
            />
            <span className="text-sm italic">I confirm that I have verified the above bank account</span>
          </div>
        </div>
        {/* Registration Instructions - full width */}
        <div>
          <Label>Additional Registration Instructions</Label>
          <textarea 
            className="w-full border rounded-lg px-3 py-2 mt-1 min-h-[80px]"
            value={formData.registration_instructions}
            onChange={(e) => setFormData({ ...formData, registration_instructions: e.target.value })}
            disabled={readOnly}
            data-testid="competition-instructions-textarea"
          />
        </div>
      </div>
    </div>
  );
}
